// Twist Tone BYOB builder.
// Category tabs (state preserved per category), tier selection, item stepper,
// sticky cart bar, and add-to-cart commit as ONE bundle line with the picks
// captured as numbered line item properties.
//
// Boots once per [data-byob] root. Re-inits after the theme editor re-renders
// the section (shopify:section:load) and follows sidebar block selection
// (shopify:block:select), so tier edits in the editor keep working.

(function () {
  'use strict';

  if (window.__byobBooted) return;
  window.__byobBooted = true;

  // Remembered across theme-editor re-renders so the merchant lands back on
  // the category they were editing instead of snapping to the first tab.
  let lastActiveCategory = null;

  function init(root) {
    if (!root || root.dataset.byobReady === 'true') return;
    root.dataset.byobReady = 'true';

    // Per-category state so switching tabs never wipes a pack you were building.
    const state = {
      activeCategory: null,
      categories: new Map(), // id -> { tier: {count, price, priceDisplay}, tierEl, selections: Map }
    };
    let submitting = false;

    function catState(id) {
      if (!state.categories.has(id)) {
        state.categories.set(id, {
          tier: { count: 0, price: 0, priceDisplay: '' },
          tierEl: null,
          selections: new Map(), // variantId -> { count, title, image }
        });
      }
      return state.categories.get(id);
    }

    function active() {
      return catState(state.activeCategory);
    }

    // ---------- selectors ----------
    const tabs = Array.from(root.querySelectorAll('[data-category]'));
    const panels = Array.from(root.querySelectorAll('[data-panel]'));
    const cartBar = root.querySelector('[data-cart]');
    const cartProgress = root.querySelector('[data-cart-progress]');
    const cartTotal = root.querySelector('[data-cart-total]');
    const cartSubmit = root.querySelector('[data-cart-submit]');
    const cartSelections = root.querySelector('[data-cart-selections]');
    const cartError = root.querySelector('[data-cart-error]');

    // The cart bar is the spine of the builder; if the section markup ever
    // changes underneath us, bail instead of throwing on every interaction.
    if (!cartBar || !cartProgress || !cartTotal || !cartSubmit) {
      console.warn('[BYOB] cart bar elements missing, builder disabled');
      return;
    }

    const defaultSubmitLabel = cartSubmit.textContent.trim();
    // Lets focus land on the bar itself when nothing better is left to focus.
    cartBar.tabIndex = -1;

    // ---------- helpers ----------
    function formatMoney(value) {
      return '$' + Number(value).toFixed(2);
    }

    function countSelections(selections) {
      let n = 0;
      selections.forEach((v) => { n += v.count; });
      return n;
    }

    function totalSelected() {
      return countSelections(active().selections);
    }

    function activePanel() {
      return root.querySelector('[data-panel="' + state.activeCategory + '"]');
    }

    function showError(message) {
      if (!cartError) return;
      cartError.textContent = message;
      cartError.hidden = false;
    }

    function clearError() {
      if (!cartError) return;
      cartError.textContent = '';
      cartError.hidden = true;
    }

    // Reserve space for the fixed bar on the DOCUMENT (not just this section) so
    // it never hides the footer, and keep keyboard-focused tiles from scrolling
    // in underneath it.
    function syncCartHeight() {
      const h = cartBar.offsetHeight;
      root.style.setProperty('--byob-cart-height', h + 'px');
      document.body.style.paddingBottom = h + 'px';
      document.documentElement.style.scrollPaddingBottom = (h + 16) + 'px';
    }

    // ---------- render ----------
    function renderCart() {
      const cs = active();
      const picked = totalSelected();
      const tierCount = cs.tier.count;
      const remaining = tierCount - picked;

      if (!submitting) {
        cartSubmit.textContent = defaultSubmitLabel;
        delete cartSubmit.dataset.busy;
      }

      if (!tierCount) {
        cartProgress.textContent = 'Pick your pack size';
        cartTotal.textContent = '';
        cartSubmit.disabled = true;
        renderSelectionPreviews();
        return;
      }

      if (remaining > 0) {
        cartProgress.textContent = picked + ' of ' + tierCount + ' picked. ' + remaining + ' to go.';
      } else {
        cartProgress.textContent = 'Pack of ' + tierCount + ' ready.';
      }
      cartTotal.textContent = (cs.tier.priceDisplay || formatMoney(cs.tier.price)) + ' total';
      cartSubmit.disabled = submitting || remaining !== 0;
      renderSelectionPreviews();
    }

    function renderSelectionPreviews() {
      if (!cartSelections) return;
      const selections = active().selections;
      cartSelections.innerHTML = '';
      if (selections.size === 0) {
        cartSelections.hidden = true;
        return;
      }
      cartSelections.hidden = false;
      selections.forEach((sel, variantId) => {
        const tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'byob__cart-selection';
        tile.setAttribute('aria-label', 'Remove all ' + sel.title + ' (' + sel.count + ')');
        tile.dataset.selectionId = variantId;

        if (sel.image) {
          const img = document.createElement('img');
          img.src = sel.image;
          img.alt = '';
          img.loading = 'lazy';
          tile.appendChild(img);
        }

        const badge = document.createElement('span');
        badge.className = 'byob__cart-selection-count';
        badge.textContent = sel.count;
        tile.appendChild(badge);

        tile.addEventListener('click', (e) => {
          if (submitting) return;
          const idx = Array.from(cartSelections.children).indexOf(tile);
          active().selections.delete(variantId);
          renderCart();
          renderItemStates();
          // Pointer taps: leave focus alone so the page never jumps.
          if (e.detail !== 0) return;
          // Keyboard: the chip just got destroyed by the rebuild. Keep focus in
          // the bar (next chip), else on the card that was removed, else the bar.
          const next = cartSelections.children[Math.min(idx, cartSelections.children.length - 1)];
          if (next) { next.focus(); return; }
          const panel = activePanel();
          const tap = panel && panel.querySelector('.byob__item[data-variant-id="' + variantId + '"] .byob__item-tap');
          if (tap && !tap.disabled) tap.focus();
          else cartBar.focus({ preventScroll: true });
        });

        cartSelections.appendChild(tile);
      });
    }

    function renderItemStates() {
      const panel = activePanel();
      if (!panel) return;
      const cs = active();
      const totalPicked = totalSelected();
      const tierFull = cs.tier.count > 0 && totalPicked >= cs.tier.count;

      root.setAttribute('data-tier-full', tierFull ? 'true' : 'false');

      panel.querySelectorAll('.byob__item[data-variant-id]').forEach((item) => {
        const variantId = item.dataset.variantId;
        const selected = cs.selections.get(variantId);
        const count = selected ? selected.count : 0;
        item.setAttribute('data-selected-count', String(count));
        const addBtn = item.querySelector('.byob__item-btn--add');
        if (addBtn) addBtn.disabled = tierFull;
        const tapBtn = item.querySelector('.byob__item-tap');
        if (tapBtn) tapBtn.disabled = tierFull;
        const countEl = item.querySelector('.byob__item-count');
        if (countEl) countEl.textContent = count > 0 ? String(count) : '';
      });
    }

    // Keyboard/AT: if the control that was just activated got hidden or disabled
    // by the render, hand focus to the card's now-usable control instead of
    // letting the browser drop it to <body>. Never moves a still-usable focus.
    function refocusIfLost(item, prev) {
      if (!prev || prev === document.body || !item.contains(prev)) return;
      if (!prev.disabled && prev.getClientRects().length > 0) return;
      const cs = active();
      const sel = cs.selections.get(item.dataset.variantId);
      const count = sel ? sel.count : 0;
      const full = cs.tier.count > 0 && totalSelected() >= cs.tier.count;
      const target = count === 0
        ? item.querySelector('.byob__item-tap')
        : item.querySelector(full ? '.byob__item-btn--remove' : '.byob__item-btn--add');
      if (target && !target.disabled) target.focus({ preventScroll: true });
    }

    // ---------- tier selection ----------
    function applyTierChecked(panel, tierBtn) {
      panel.querySelectorAll('[data-tier-count]').forEach((b) => {
        const on = b === tierBtn;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
    }

    // Changing pack size keeps every pick that still fits. Only when the new
    // pack is smaller are the most recent picks trimmed until it fits.
    function trimSelections(cs) {
      const limit = cs.tier.count;
      if (!limit) return;
      let picked = countSelections(cs.selections);
      const keys = Array.from(cs.selections.keys());
      while (picked > limit && keys.length) {
        const key = keys[keys.length - 1];
        const sel = cs.selections.get(key);
        if (sel.count > 1) {
          sel.count -= 1;
        } else {
          cs.selections.delete(key);
          keys.pop();
        }
        picked -= 1;
      }
    }

    function selectTier(tierBtn) {
      const panel = tierBtn.closest('[data-panel]');
      if (!panel || tierBtn.disabled || submitting) return;
      const cs = catState(panel.dataset.panel);
      // Radio semantics: re-selecting the checked tier is a no-op. It must never
      // wipe the pack (an easy accidental tap on a phone).
      if (cs.tierEl === tierBtn) {
        applyTierChecked(panel, tierBtn);
        return;
      }
      applyTierChecked(panel, tierBtn);
      cs.tier = {
        count: Number(tierBtn.dataset.tierCount) || 0,
        price: Number(tierBtn.dataset.tierPrice) || 0,
        priceDisplay: tierBtn.dataset.tierPriceDisplay || '',
      };
      cs.tierEl = tierBtn;
      trimSelections(cs);
      clearError();
      if (panel.dataset.panel === state.activeCategory) {
        renderCart();
        renderItemStates();
      }
    }

    // ---------- category switching (non-destructive) ----------
    function switchCategory(categoryId) {
      if (!categoryId || state.activeCategory === categoryId) return;
      state.activeCategory = categoryId;
      lastActiveCategory = categoryId;

      tabs.forEach((tab) => {
        const on = tab.dataset.category === categoryId;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.panel !== categoryId;
      });

      clearError();

      const cs = active();
      const panel = activePanel();
      if (!cs.tierEl) {
        // First visit to this category: default to its first usable tier.
        const firstTier = panel && panel.querySelector('[data-tier-count]:not([disabled])');
        if (firstTier) {
          selectTier(firstTier);
          syncCartHeight();
          return;
        }
      } else if (panel) {
        // Returning to a category: restore the tier we had selected.
        applyTierChecked(panel, cs.tierEl);
      }
      renderCart();
      renderItemStates();
      syncCartHeight();
    }
    root.__byobSwitch = switchCategory;

    // ---------- item stepper ----------
    function addItem(item) {
      if (submitting) return;
      const cs = active();
      const variantId = item.dataset.variantId;
      const title = item.dataset.variantTitle || '';
      const imgEl = item.querySelector('.byob__item-media img');
      const image = imgEl ? imgEl.currentSrc || imgEl.src : '';
      if (cs.tier.count > 0 && totalSelected() >= cs.tier.count) return;
      const existing = cs.selections.get(variantId);
      if (existing) {
        existing.count += 1;
      } else {
        cs.selections.set(variantId, { count: 1, title, image });
      }
      const prev = document.activeElement;
      clearError();
      renderCart();
      renderItemStates();
      refocusIfLost(item, prev);
    }

    function removeItem(item) {
      if (submitting) return;
      const cs = active();
      const variantId = item.dataset.variantId;
      const existing = cs.selections.get(variantId);
      if (!existing) return;
      if (existing.count > 1) {
        existing.count -= 1;
      } else {
        cs.selections.delete(variantId);
      }
      const prev = document.activeElement;
      renderCart();
      renderItemStates();
      refocusIfLost(item, prev);
    }

    // ---------- keyboard nav ----------
    function focusTabAt(index) {
      if (!tabs.length) return;
      const clamped = (index + tabs.length) % tabs.length;
      tabs.forEach((t, i) => { t.tabIndex = i === clamped ? 0 : -1; });
      tabs[clamped].focus();
    }

    // ---------- wire up ----------
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => switchCategory(tab.dataset.category));
      // Manual activation: arrows move focus, Enter/Space (native) switches the tab.
      tab.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); focusTabAt(i + 1); }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); focusTabAt(i - 1); }
        else if (e.key === 'Home') { e.preventDefault(); focusTabAt(0); }
        else if (e.key === 'End') { e.preventDefault(); focusTabAt(tabs.length - 1); }
      });
    });

    root.querySelectorAll('[data-tier-count]').forEach((tierBtn) => {
      tierBtn.addEventListener('click', () => selectTier(tierBtn));
      // Radiogroup: arrows move and select in one step (non-destructive now).
      tierBtn.addEventListener('keydown', (e) => {
        const panel = tierBtn.closest('[data-panel]');
        if (!panel) return;
        const arr = Array.from(panel.querySelectorAll('[data-tier-count]:not([disabled])'));
        const i = arr.indexOf(tierBtn);
        if (i === -1) return;
        let target = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') target = arr[(i + 1) % arr.length];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') target = arr[(i - 1 + arr.length) % arr.length];
        else if (e.key === 'Home') target = arr[0];
        else if (e.key === 'End') target = arr[arr.length - 1];
        if (target) {
          e.preventDefault();
          selectTier(target);
          target.focus();
        }
      });
    });

    root.querySelectorAll('.byob__item[data-variant-id]').forEach((item) => {
      const tapBtn = item.querySelector('.byob__item-tap');
      const addBtn = item.querySelector('.byob__item-btn--add');
      const removeBtn = item.querySelector('.byob__item-btn--remove');
      if (tapBtn) tapBtn.addEventListener('click', () => addItem(item));
      if (addBtn) addBtn.addEventListener('click', () => addItem(item));
      if (removeBtn) removeBtn.addEventListener('click', () => removeItem(item));
    });

    async function commitToCart() {
      if (submitting) return;
      const cs = active();
      if (cs.selections.size === 0) return;
      if (cs.tier.count > 0 && totalSelected() !== cs.tier.count) return;

      // One cart line: the category's bundle variant for the chosen tier, with the
      // customer's picks captured as numbered line item properties. A hidden
      // "_picks" property carries the variant ids for fulfilment tooling.
      const bundleVariantId = cs.tierEl && cs.tierEl.dataset.bundleVariantId;
      if (!bundleVariantId) {
        showError('This pack size is not set up yet. Try another size or check back soon.');
        return;
      }
      const properties = {};
      const picks = [];
      let pick = 1;
      cs.selections.forEach((sel, variantId) => {
        picks.push({ id: variantId, qty: sel.count });
        for (let k = 0; k < sel.count; k++) {
          properties['Pick ' + pick] = sel.title;
          pick++;
        }
      });
      properties._picks = JSON.stringify(picks);
      const items = [{ id: bundleVariantId, quantity: 1, properties: properties }];

      // Dawn exposes locale-aware routes; fall back to the platform root.
      const routes = window.routes || {};
      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const addUrl = (routes.cart_add_url || rootUrl + 'cart/add') + '.js';
      const cartUrl = routes.cart_url || rootUrl + 'cart';

      submitting = true;
      clearError();
      cartSubmit.disabled = true;
      cartSubmit.dataset.busy = 'true';
      cartSubmit.textContent = 'Adding...';

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          let errorMessage = 'Could not add to cart. Please try again.';
          try {
            const errBody = await response.json();
            if (errBody && errBody.description) errorMessage = errBody.description;
            else if (errBody && errBody.message) errorMessage = errBody.message;
          } catch (_) {}
          throw new Error(errorMessage);
        }

        // In the theme editor preview, redirects are blocked. Stay put and confirm.
        if (window.Shopify && window.Shopify.designMode) {
          submitting = false;
          cs.selections.clear();
          renderCart();
          renderItemStates();
          cartSubmit.disabled = true;
          cartSubmit.textContent = 'Added to cart';
          return;
        }

        window.location.href = cartUrl;
      } catch (err) {
        console.error('[BYOB] Cart add failed:', err);
        submitting = false;
        renderCart();
        showError(err instanceof TypeError
          ? 'Connection problem. Check your signal and try again.'
          : err.message);
      }
    }

    cartSubmit.addEventListener('click', commitToCart);

    // Coming back from the cart via the browser's back/forward cache would
    // otherwise restore a button stuck on "Adding...".
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      submitting = false;
      renderCart();
      renderItemStates();
    });

    // ---------- init ----------
    if ('ResizeObserver' in window) {
      new ResizeObserver(syncCartHeight).observe(cartBar);
    }
    window.addEventListener('resize', syncCartHeight);

    // Deep link: /pages/build-your-bundle?cat=guitar (or #guitar) opens that
    // category. Matches the block's collection handle, fully or by substring.
    function tabFromLocation() {
      let key = '';
      try {
        key = new URLSearchParams(window.location.search).get('cat') || window.location.hash.replace('#', '');
      } catch (_) {}
      key = (key || '').trim().toLowerCase();
      if (!key) return null;
      const handle = (t) => (t.dataset.collection || '').toLowerCase();
      return tabs.find((t) => handle(t) === key || t.dataset.category === key)
        || tabs.find((t) => handle(t).indexOf(key) !== -1)
        || null;
    }

    const rememberedTab = lastActiveCategory && tabs.find((t) => t.dataset.category === lastActiveCategory);
    const startTab = rememberedTab || tabFromLocation() || tabs[0];
    if (startTab) {
      switchCategory(startTab.dataset.category);
    }
    syncCartHeight();
  }

  document.querySelectorAll('[data-byob]').forEach(init);

  // Theme editor: the section is re-rendered on every setting change.
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-byob]').forEach(init);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    if (!e.target.querySelector('[data-byob]')) return;
    document.body.style.paddingBottom = '';
    document.documentElement.style.scrollPaddingBottom = '';
  });
  // Theme editor: clicking a Category block in the sidebar shows that panel.
  document.addEventListener('shopify:block:select', (e) => {
    const tab = e.target && e.target.closest ? e.target.closest('[data-category]') : null;
    const root = tab && tab.closest('[data-byob]');
    if (root && root.__byobSwitch) root.__byobSwitch(tab.dataset.category);
  });
})();
